import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, KeyRound, PlayCircle, Award, GraduationCap, Trophy, BookOpen } from "lucide-react";
import Navbar from "@/components/Navbar";
import VRViewer from "@/components/VRViewer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import api, { imgUrl, apiError } from "@/lib/api";

export default function StudentDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [linked, setLinked] = useState(false);
  const [teacherName, setTeacherName] = useState(null);
  const [rosterId, setRosterId] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [progress, setProgress] = useState({ attempts: [], avg: 0, completed: 0 });
  const [code, setCode] = useState("");
  const [joining, setJoining] = useState(false);
  const [launch, setLaunch] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/student/lessons");
      setLinked(data.linked);
      setTeacherName(data.teacher_name);
      setRosterId(data.roster_student_id);
      setLessons(data.lessons || []);
      if (data.linked) {
        const p = await api.get("/student/progress");
        setProgress(p.data);
      }
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const join = async () => {
    if (!code.trim()) return;
    setJoining(true);
    try {
      const { data } = await api.post("/student/join", { code: code.trim() });
      toast.success(`Joined ${data.teacher_name}'s class!`);
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Invalid class code");
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background"><Navbar />
        <div className="flex items-center justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight">Hi {user?.name?.split(" ")[0]} 👋</h1>
        <p className="text-muted-foreground mt-1">{linked ? `${teacherName}'s class` : "Join your class to start exploring VR lessons"}</p>

        {!linked ? (
          <div className="max-w-md mx-auto mt-16 rounded-3xl border border-border bg-card p-8 text-center">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto"><KeyRound className="w-7 h-7" /></div>
            <h2 className="text-xl font-display font-bold mt-4">Enter your class code</h2>
            <p className="text-sm text-muted-foreground mt-2">Ask your teacher for the 8-character code shown on their dashboard.</p>
            <Input data-testid="student-join-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. A1B2C3D4" maxLength={8} className="mt-5 h-12 text-center text-lg font-mono tracking-widest" />
            <Button data-testid="student-join-btn" onClick={join} disabled={joining} className="mt-4 w-full h-11 rounded-full">
              {joining ? <Loader2 className="w-5 h-5 animate-spin" /> : "Join class"}
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-8">
              <div className="rounded-2xl border border-border bg-card p-5"><div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><BookOpen className="w-5 h-5" /></div><div className="text-3xl font-display font-extrabold mt-3">{lessons.length}</div><div className="text-sm text-muted-foreground">Lessons available</div></div>
              <div className="rounded-2xl border border-border bg-card p-5"><div className="w-11 h-11 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center"><Trophy className="w-5 h-5" /></div><div className="text-3xl font-display font-extrabold mt-3">{progress.completed}</div><div className="text-sm text-muted-foreground">Quizzes completed</div></div>
              <div className="rounded-2xl border border-border bg-card p-5"><div className="w-11 h-11 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center"><Award className="w-5 h-5" /></div><div className="text-3xl font-display font-extrabold mt-3">{progress.avg}%</div><div className="text-sm text-muted-foreground">Avg quiz score</div></div>
            </div>

            <h2 className="font-display font-bold text-xl mt-10 mb-4 flex items-center gap-2"><GraduationCap className="w-5 h-5 text-primary" /> Your VR lessons</h2>
            {lessons.length === 0 ? (
              <p className="text-muted-foreground">No lessons yet — check back when your teacher publishes one.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {lessons.map((l) => {
                  const cover = l.vr_scene?.scenes?.[0]?.backdrop_url || l.documents?.storyboard?.[0]?.image_url;
                  return (
                    <div key={l.id} data-testid={`student-lesson-${l.id}`} className="rounded-2xl border border-border bg-card overflow-hidden hover:shadow-xl transition-shadow">
                      <div className="aspect-video bg-secondary relative">
                        {cover ? <img src={imgUrl(cover)} alt={l.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><PlayCircle className="w-8 h-8" /></div>}
                      </div>
                      <div className="p-4">
                        <div className="font-semibold truncate">{l.title}</div>
                        <div className="text-xs text-muted-foreground">{l.subject} · {l.grade}</div>
                        <Button data-testid={`student-launch-${l.id}`} onClick={() => setLaunch(l)} className="mt-3 w-full rounded-full h-9"><PlayCircle className="w-4 h-4 mr-1" /> Enter lesson</Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {progress.attempts.length > 0 && (
              <div className="mt-10">
                <h2 className="font-display font-bold text-xl mb-4 flex items-center gap-2"><Award className="w-5 h-5 text-amber-600" /> My scores</h2>
                <div className="space-y-2">
                  {progress.attempts.map((a) => (
                    <div key={a.id} className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3">
                      <span className="text-sm font-medium truncate">{a.lesson_title}</span>
                      <Badge className={`${a.percent >= 60 ? "bg-emerald-500/15 text-emerald-700" : "bg-amber-500/15 text-amber-700"} border-0 font-bold`}>{a.score}/{a.total} · {a.percent}%</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Dialog open={!!launch} onOpenChange={(o) => { if (!o) { setLaunch(null); load(); } }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>{launch?.title}</DialogTitle>
            <DialogDescription>Explore the scene, then tap “Take the quiz” — your score saves automatically.</DialogDescription>
          </DialogHeader>
          <div className="rounded-2xl overflow-hidden border border-slate-800 bg-[#0B0F19] h-[460px]" data-testid="student-vr-container">
            {launch && <VRViewer scene={launch.vr_scene} quiz={launch.documents?.assessment?.questions || []} lessonId={launch.id} defaultStudentId={rosterId} onQuizSubmitted={load} />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
